import { logEvent } from 'firebase/analytics';
import { analytics } from './firebase';
import { getCurrentOrgName } from './helpers';

/**
 * Checks if we're running on client side
 * @returns {boolean}
 */
const isClient = () => typeof window !== 'undefined';

let clevertap;
let clevertapReady = false;
let clevertapReadyCallbacks = [];

const onClevertapReady = (fn) => {
  if (clevertapReady) {
    fn();
  } else {
    clevertapReadyCallbacks.push(fn);
  }
};

/**
 * Initialize CleverTap SDK
 */
export const initializeAnalytics = async () => {
  if (!isClient()) return;

  try {
    const module = await import('clevertap-web-sdk');
    clevertap = module.default;
    const orgName = getCurrentOrgName();
    let CLEVERTAP_ACCOUNT_ID = process.env.REACT_APP_PUBLIC_NEXTIAS_CLEVERTAP_ACCOUNT_ID;
    if (orgName === 'nextias') {
      CLEVERTAP_ACCOUNT_ID = process.env.REACT_APP_PUBLIC_NEXTIAS_CLEVERTAP_ACCOUNT_ID;
    } else if (orgName === 'madeeasyprime') {
      CLEVERTAP_ACCOUNT_ID = process.env.REACT_APP_PUBLIC_MADEEASY_CLEVERTAP_ACCOUNT_ID;
    }

    if (!CLEVERTAP_ACCOUNT_ID) {
      throw new Error('CleverTap Account ID is not configured');
    }

    clevertap.privacy.push({ optOut: false });
    clevertap.privacy.push({ useIP: true });
    clevertap.init(CLEVERTAP_ACCOUNT_ID);
    clevertap.setLogLevel(3);

    clevertapReady = true;
    clevertapReadyCallbacks.forEach((fn) => fn());
    clevertapReadyCallbacks = [];
  } catch (error) {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to initialize CleverTap:', error);
    }
  }
};

let currentUser = {};

export const setAnalyticsUser = (user) => {
  currentUser = {
    userName: user?.name,
    userEmail: user?.email,
    phone: user?.phone,
  };
};

/**
 * Generic event tracking function that sends events to both Firebase and CleverTap
 * @param {string} eventName - Name of the event
 * @param {Object} eventProperties - Properties/parameters for the event
 */
export const trackEvent = async (eventName, eventProperties = {}) => {
  if (!isClient()) return; // Skip if running on server

  try {
    const updatedProperties = {
      ...eventProperties,
      ...currentUser,
      timestamp: new Date().getTime(),
    };
    // Send to Firebase (analytics will be undefined on server)
    if (analytics) {
      logEvent(analytics, eventName, updatedProperties);
    }

    // Send to CleverTap
    onClevertapReady(() => {
      if (clevertap?.event) {
        clevertap.event.push(eventName, updatedProperties);
      }
    });
  } catch (error) {
    // Silently handle error in production
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.error('Error tracking event:', error);
    }
  }
};

/**
 * Track user profile updates
 * @param {Object} userProperties - User properties to update
 */
export const trackUserProfile = async (userProperties) => {
  if (!isClient()) return; // Skip if running on server

  try {
    // Update CleverTap user profile
    onClevertapReady(() => {
      if (clevertap?.profile) {
        clevertap.profile.push({ Site: { ...userProperties } });
      }
    });

    // Set user properties in Firebase
    if (analytics) {
      logEvent(analytics, 'user_profile_update', userProperties);
    }
  } catch (error) {
    // Silently handle error in production
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating user profile:', error);
    }
  }
};

export const onUserLogin = (user) => {
  if (!isClient()) return;
  if (!user?.name || !user?.email) return;

  const phone = user?.phone ? `+91${user.phone}` : undefined;

  onClevertapReady(() => {
    try {
      if (clevertap?.onUserLogin) {
        const profile = {
          Name: user.name,
          Email: user.email,
        };
        if (phone) {
          profile.Phone = phone;
        }
        clevertap.onUserLogin.push({ Site: profile });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') {
        console.error('Error tracking user login:', error);
      }
    }
  });
};
